import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMap,
} from "@vis.gl/react-google-maps";

import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  MIN_ZOOM,
  MAX_ZOOM,
  latitudeDeltaToZoom,
  getMapColors,
  darkMapStyle,
  lightMapStyle,
} from "./map-styles";
import {
  Box,
  colorVar,
  Icon,
  MAP_MARKER_EXIT_MS,
  MapClusterGlyph,
  MapInfoboxCaret,
  MapPinGlyph,
  type IconName,
} from "@salestrak/ui";
import { UserLocationMarker } from "./user-location-marker";
import { useMarkerPresence } from "./use-marker-presence";
import { MapInfoboxContent, INFOBOX_WIDTH } from "./map-infobox-content";
import { getClusterAtts } from "./map-utils";
import type {
  MapPin,
  MapClusterBubble,
  Region,
  MapViewRef,
  NativeMapViewProps,
} from "./map-types";
import { getEnv } from "#app/lib/env";
import { omitUndefined, type Omittable } from "@salestrak/ui";

export type { MapPin, MapClusterBubble, Region, MapViewRef } from "./map-types";

const API_KEY = getEnv("EXPO_PUBLIC_GOOGLE_API_KEY");
const MAP_ID = "90f87356969d889c";

// How long a removed marker stays mounted to play its fade/shrink-out before
// being dropped. Matches the map-marker particle's exit duration.
const MARKER_EXIT_MS = MAP_MARKER_EXIT_MS;

const getPinKey = (pin: MapPin) => pin.id;
const getClusterKey = (cluster: MapClusterBubble) => cluster.id;

// Registering a (no-op) click listener makes the marker clickable, which is
// what tells vis.gl to enable pointer-events on the infobox content.
// Module-scope so the listener isn't re-registered every render.
const consumeMarkerClick = () => {};

// Stop the infobox's presses from reaching the map. Google Maps synthesizes
// its map `click` from the down/up events that bubble to its container, so
// stopping those here means presses inside the infobox can never be taken
// for map clicks (which create a pin). DOM `click` itself must keep
// bubbling — React's delegated handlers for the card's buttons sit above
// the map container. Listeners die with the node; no cleanup needed.
const CONTAINED_EVENTS = [
  "pointerdown", "pointerup", "mousedown", "mouseup",
  "touchstart", "touchend", "dblclick",
] as const;
const containMapEvents = (node: any) => {
  if (!node) return;
  for (const type of CONTAINED_EVENTS) {
    node.addEventListener(type, (e: Event) => e.stopPropagation());
  }
};

// The overlay glyphs on a pin's disc are painted with the map palette's
// resolved `pinOverlayText` rather than a theme token: the disc fill beneath
// them comes from the same non-themed palette, so a token would not track it.
// Icon paints `currentColor`, so the value rides in as `color`.
const overlayIconStyle = (color: string) => ({ color });

function NewLocationPinMarker({
  latitude,
  longitude,
  outlineColor,
  pinOverlay,
  pinOverlayText,
}: {
  latitude: number;
  longitude: number;
  outlineColor: string;
  pinOverlay: string;
  pinOverlayText: string;
}) {
  return (
    <AdvancedMarker
      position={{ lat: latitude, lng: longitude }}
      zIndex={999}
    >
      <MapPinGlyph
        color="var(--primary)"
        outlineColor={outlineColor}
        overlayColor={pinOverlay}
      >
        <Icon name="plus" size="lg" style={overlayIconStyle(pinOverlayText)} />
      </MapPinGlyph>
    </AdvancedMarker>
  );
}

// ─── Marker components ───────────────────────────────────────────────────────

function PinMarker({
  pin,
  outlineColor,
  pinOverlay,
  pinOverlayText,
  onClick,
  exiting,
}: Omittable<{
  pin: MapPin;
  outlineColor: string;
  pinOverlay: string;
  pinOverlayText: string;
  onClick?: () => void;
  exiting?: boolean;
}>) {
  const showIcon = pin.count === 1 && !!pin.icon;
  const displayText = pin.count > 9 ? "9+" : String(pin.count);
  const countFontSize = pin.count > 9 ? 11 : 14;

  return (
    // `omitUndefined` because a non-clickable marker must DELETE `onClick` —
    // `AdvancedMarker`'s props predate `exactOptionalPropertyTypes`.
    <AdvancedMarker
      {...omitUndefined({
        position: { lat: pin.latitude, lng: pin.longitude },
        onClick: exiting ? undefined : onClick,
      })}
    >
      <MapPinGlyph
        color={colorVar(pin.color)}
        exiting={exiting}
        label={showIcon ? undefined : displayText}
        labelColor={pinOverlayText}
        labelFontSize={countFontSize}
        outlineColor={outlineColor}
        overlayColor={pinOverlay}
      >
        {/* Workflow icon overlay — the particle centers it on the disc. */}
        {showIcon ? (
          <Icon
            name={pin.icon as IconName}
            size="lg"
            style={overlayIconStyle(pinOverlayText)}
          />
        ) : null}
      </MapPinGlyph>
    </AdvancedMarker>
  );
}

function ClusterMarker({
  cluster,
  outlineColor,
  clusterFill,
  clusterText,
  onClick,
  exiting,
}: Omittable<{
  cluster: MapClusterBubble;
  outlineColor: string;
  clusterFill: string;
  clusterText: string;
  onClick: (bounds: MapClusterBubble["bounds"]) => void;
  exiting?: boolean;
}>) {
  const { dimension, fontSize, strokeWidth } = getClusterAtts(cluster.count);

  return (
    // `omitUndefined` because a non-clickable marker must DELETE `onClick` —
    // `AdvancedMarker`'s props predate `exactOptionalPropertyTypes`.
    <AdvancedMarker
      {...omitUndefined({
        position: { lat: cluster.latitude, lng: cluster.longitude },
        onClick: exiting ? undefined : () => onClick(cluster.bounds),
        zIndex: 1,
      })}
    >
      <MapClusterGlyph
        color={clusterFill}
        count={cluster.count}
        exiting={exiting}
        fontSize={fontSize}
        outlineColor={outlineColor}
        size={dimension}
        strokeWidth={strokeWidth}
        textColor={clusterText}
      />
    </AdvancedMarker>
  );
}

// React 19 ref-as-prop — the app does not use forwardRef.
function MapInternals({
  isDark,
  mapSettings,
  pins,
  clusters,
  onRegionChangeComplete,
  onInteractionStart,
  onClusterPress,
  onPinPress,
  userLocation,
  selectedPin,
  onInfoboxClose,
  onAddProject,
  onMapClick,
  ref,
}: NativeMapViewProps & Omittable<{ ref?: React.Ref<MapViewRef> }>) {
  const map = useMap();
  // Queue imperative calls that fire before `map` is ready (e.g. when the
  // map screen mounts via cross-route navigation and the consumer invokes
  // focusWithOffset on the very first render). Drained when map becomes
  // non-null.
  const pendingCallsRef = useRef<Array<(m: google.maps.Map) => void>>([]);
  useEffect(() => {
    if (!map) return;
    const queued = pendingCallsRef.current;
    pendingCallsRef.current = [];
    queued.forEach((fn) => fn(map));
  }, [map]);
  const mapColors = getMapColors(isDark);
  // Keep removed markers mounted long enough to animate out (enter animation
  // is handled per-marker by the glyph's own transition on mount).
  const clusterPresence = useMarkerPresence(clusters, getClusterKey, MARKER_EXIT_MS);
  const pinPresence = useMarkerPresence(pins, getPinKey, MARKER_EXIT_MS);
  const onRegionChangeRef = useRef(onRegionChangeComplete);
  onRegionChangeRef.current = onRegionChangeComplete;
  const onInteractionStartRef = useRef(onInteractionStart);
  onInteractionStartRef.current = onInteractionStart;
  const onMapClickRef = useRef(onMapClick);
  onMapClickRef.current = onMapClick;
  const selectedPinRef = useRef(selectedPin);
  selectedPinRef.current = selectedPin;

  const handleAddProject = useCallback(() => {
    if (selectedPinRef.current && onAddProject) {
      onAddProject(selectedPinRef.current);
    }
  }, [onAddProject]);

  // Apply map styles via StyledMapType on mount. The parent Map component
  // remounts (via key) when settings change, so this runs fresh each time
  // with the correct isDark + mapSettings values.
  useEffect(() => {
    if (!map) return;

    if (mapSettings.mapTypeId === "hybrid") {
      map.setMapTypeId("hybrid");
      return;
    }

    const themeStyles = isDark ? darkMapStyle : lightMapStyle;
    const labelStyles = [
      {
        featureType: "road",
        elementType: "labels",
        stylers: [{ visibility: mapSettings.roadNames ? "on" : "off" }],
      },
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: mapSettings.pointsOfInterest ? "on" : "off" }],
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: mapSettings.transitLines ? "on" : "off" }],
      },
    ];
    const styledMapType = new google.maps.StyledMapType([
      ...themeStyles,
      ...labelStyles,
    ] as any);
    map.mapTypes.set("customized", styledMapType);
    map.setMapTypeId("customized" as any);
  }, [map, isDark, mapSettings]);

  useImperativeHandle(
    ref,
    () => ({
      panTo(coords: { latitude: number; longitude: number }) {
        const run = (m: google.maps.Map) =>
          m.panTo({ lat: coords.latitude, lng: coords.longitude });
        if (!map) {
          pendingCallsRef.current.push(run);
          return;
        }
        run(map);
      },
      animateToRegion(region: Region, _duration?: number) {
        if (!map) {
          pendingCallsRef.current.push((m) => {
            m.panTo({ lat: region.latitude, lng: region.longitude });
            m.setZoom(latitudeDeltaToZoom(region.latitudeDelta));
          });
          return;
        }
        const fromCenter = map.getCenter();
        if (!fromCenter) return;

        const fromZoom = map.getZoom() ?? DEFAULT_ZOOM;
        const toZoom = latitudeDeltaToZoom(region.latitudeDelta);
        const span = Math.max(
          Math.abs(region.latitude - fromCenter.lat()),
          Math.abs(region.longitude - fromCenter.lng()),
        );

        const mapInstance = map;
        // ~0.3° ≈ city-size area — smooth fly-to within this range
        if (span < 0.3) {
          mapInstance.panTo({ lat: region.latitude, lng: region.longitude });
          if (Math.abs(fromZoom - toZoom) > 0.5) {
            mapInstance.setZoom(toZoom);
          }
        } else {
          // Far away: snap to position zoomed out slightly, then ease in
          // Floor to integer so we land on a clean tile level (avoids mid-transition tile color flicker)
          const landingZoom = Math.floor(toZoom);
          mapInstance.moveCamera({
            center: { lat: region.latitude, lng: region.longitude },
            zoom: landingZoom,
          });
          // Ease zoom in over 400ms
          const start = performance.now();
          function step(now: number) {
            const t = Math.min((now - start) / 400, 1);
            const ease = 1 - (1 - t) * (1 - t); // ease-out quad
            mapInstance.moveCamera({
              center: { lat: region.latitude, lng: region.longitude },
              zoom: landingZoom + (toZoom - landingZoom) * ease,
            });
            if (t < 1) requestAnimationFrame(step);
          }
          requestAnimationFrame(step);
        }
      },
      fitToCoordinates(
        coords: Array<{ latitude: number; longitude: number }>,
        options?: {
          edgePadding?: {
            top: number;
            right: number;
            bottom: number;
            left: number;
          };
        },
      ) {
        if (!map) return;
        const bounds = new google.maps.LatLngBounds();
        coords.forEach((c) =>
          bounds.extend({ lat: c.latitude, lng: c.longitude }),
        );
        const padding = options?.edgePadding ?? 50;
        map.fitBounds(bounds, padding);
      },
      focusWithOffset(
        target: { latitude: number; longitude: number },
        offsetX: number,
        zoom?: number,
      ) {
        const run = (m: google.maps.Map) => {
          if (zoom !== undefined) m.setZoom(zoom);
          m.panTo({ lat: target.latitude, lng: target.longitude });
          if (offsetX !== 0) {
            const shift = () => m.panBy(offsetX / 2, 0);
            if (typeof window !== "undefined") {
              requestAnimationFrame(shift);
            } else {
              shift();
            }
          }
        };
        if (!map) {
          pendingCallsRef.current.push(run);
          return;
        }
        run(map);
      },
      zoomIn() {
        if (!map) return;
        const current = map.getZoom() ?? DEFAULT_ZOOM;
        map.setZoom(Math.min(Math.round(current) + 1, MAX_ZOOM));
      },
      zoomOut() {
        if (!map) return;
        const current = map.getZoom() ?? DEFAULT_ZOOM;
        map.setZoom(Math.max(Math.round(current) - 1, MIN_ZOOM));
      },
    }),
    [map],
  );

  // Notify parent when user starts interacting (drag or zoom) so it can
  // freeze displayed markers until the map settles.
  useEffect(() => {
    if (!map) return;
    const handleStart = () => onInteractionStartRef.current?.();
    const dragListener = map.addListener("dragstart", handleStart);
    const zoomListener = map.addListener("zoom_changed", handleStart);
    return () => {
      dragListener.remove();
      zoomListener.remove();
    };
  }, [map]);

  // Sync region state when map becomes idle (after drag/zoom)
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      const center = map.getCenter();
      const mapBounds = map.getBounds();
      if (!center || !mapBounds) return;
      const ne = mapBounds.getNorthEast();
      const sw = mapBounds.getSouthWest();
      onRegionChangeRef.current({
        latitude: center.lat(),
        longitude: center.lng(),
        latitudeDelta: ne.lat() - sw.lat(),
        longitudeDelta: ne.lng() - sw.lng(),
        zoom: map.getZoom(),
      });
    });
    return () => listener.remove();
  }, [map]);

  // Forward map clicks on empty space to parent. Clicks on markers (pins,
  // clusters, the infobox) never reach this listener — they are clickable
  // markers, so Google's hit-testing consumes them.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("click", (e: google.maps.MapMouseEvent) => {
      const latLng = e.latLng;
      if (!latLng) return;
      onMapClickRef.current?.({
        latitude: latLng.lat(),
        longitude: latLng.lng(),
      });
    });
    return () => listener.remove();
  }, [map]);

  return (
    <>
      {clusterPresence.map(({ key, item: cluster, exiting }) => (
        <ClusterMarker
          key={key}
          cluster={cluster}
          outlineColor={mapColors.outlineColor}
          clusterFill={mapColors.clusterFill}
          clusterText={mapColors.clusterText}
          onClick={onClusterPress}
          exiting={exiting}
        />
      ))}
      {pinPresence.map(({ key, item: pin, exiting }) => (
        <PinMarker
          key={key}
          pin={pin}
          outlineColor={mapColors.outlineColor}
          pinOverlay={mapColors.pinOverlay}
          pinOverlayText={mapColors.pinOverlayText}
          onClick={() => onPinPress?.(pin)}
          exiting={exiting}
        />
      ))}
      {userLocation && (
        <UserLocationMarker
          coordinate={{
            latitude: userLocation.latitude,
            longitude: userLocation.longitude,
          }}
        />
      )}

      {/* Local pin markers (created via map click, not in GraphQL data) */}
      {selectedPin?.isLocal && selectedPin.issueIds.length === 0 && (
        <NewLocationPinMarker
          latitude={selectedPin.latitude}
          longitude={selectedPin.longitude}
          outlineColor={mapColors.outlineColor}
          pinOverlay={mapColors.pinOverlay}
          pinOverlayText={mapColors.pinOverlayText}
        />
      )}
      {selectedPin?.isLocal && selectedPin.issueIds.length > 0 && (
        <PinMarker
          pin={selectedPin}
          outlineColor={mapColors.outlineColor}
          pinOverlay={mapColors.pinOverlay}
          pinOverlayText={mapColors.pinOverlayText}
        />
      )}

      {/* Infobox overlay for selected pin (desktop only).
           Uses AdvancedMarker instead of InfoWindow to avoid the
           Google Maps InfoWindow open/close animation flicker.
           containMapEvents on the card stops its presses from bubbling to
           the map, so they can't be taken for map clicks. */}
      {selectedPin && (
        <AdvancedMarker
          position={{
            lat: selectedPin.latitude,
            lng: selectedPin.longitude,
          }}
          zIndex={1000}
          onClick={consumeMarkerClick}
        >
          <Box bottom={59} left={-(INFOBOX_WIDTH / 2)} position="absolute">
            {/* id: stable hook for the e2e suite's infobox assertions — the
                overlay lives inside the Google Maps DOM, where structural
                selectors aren't stable. */}
            <Box
              className={isDark ? undefined : "drop-shadow-md"}
              id="map-infobox"
              ref={containMapEvents}
            >
              <MapInfoboxContent
                pin={selectedPin}
                onClose={onInfoboxClose}
                onAddProject={onAddProject ? handleAddProject : undefined}
              />
              <MapInfoboxCaret />
            </Box>
          </Box>
        </AdvancedMarker>
      )}
    </>
  );
}

export function NativeMapView({
  ref,
  ...props
}: NativeMapViewProps & Omittable<{ ref?: React.Ref<MapViewRef> }>) {
  const { mapSettings, isDark, initialRegion } = props;
  const { mapBg } = getMapColors(isDark);
  // Build a key that forces a full Map remount when settings change
  const mapKey = `${isDark}-${mapSettings.mapTypeId}-${mapSettings.roadNames}-${mapSettings.pointsOfInterest}-${mapSettings.transitLines}`;

  // Captured once per mount — the Map only reads `defaultCenter`/`defaultZoom` on first render.
  const initialMapStateRef = useRef({
    center: initialRegion
      ? { lat: initialRegion.latitude, lng: initialRegion.longitude }
      : { lat: DEFAULT_CENTER.latitude, lng: DEFAULT_CENTER.longitude },
    zoom: initialRegion
      ? (initialRegion.zoom ?? Math.round(latitudeDeltaToZoom(initialRegion.latitudeDelta)))
      : DEFAULT_ZOOM,
  });

  return (
    <Box background="background" fill="flex" flex={1}>
      <APIProvider apiKey={API_KEY} libraries={["places"]}>
        <Map
          key={mapKey}
          mapId={MAP_ID}
          defaultCenter={initialMapStateRef.current.center}
          defaultZoom={initialMapStateRef.current.zoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          gestureHandling="greedy"
          disableDefaultUI
          disableDoubleClickZoom
          clickableIcons={false}
          backgroundColor={mapBg}
        >
          <MapInternals ref={ref} {...props} />
        </Map>
      </APIProvider>
    </Box>
  );
}
